<template>
  <span class="html-mobile-cards-anchor" aria-hidden="true"></span>
  <Teleport v-if="portalTarget" :to="portalTarget">
    <section class="html-mobile-cards-list"
      :class="{ 'html-mobile-cards-grouped': groupRows }"
      :style="{ width: `calc(100% + ${horizontalOffset * 2}px)`, marginLeft: `-${horizontalOffset}px` }"
      :aria-label="`${tableId} mobile results`"
    >
      <h5 v-if="groupRows && groupLabel && cards.length" class="html-mobile-cards-group-title">{{ groupLabel }}</h5>
      <article v-for="card in cards" :key="card.key" class="html-mobile-card">
        <div v-if="card.selection" class="html-mobile-card-header">
          <label class="checkbox checkbox-single html-mobile-card-checkbox">
            <input v-bind="card.selection.attrs" type="checkbox" :checked="card.selection.checked" @change="handleSelectionChange($event, card)" />
            <span></span>
          </label>
        </div>
        <div class="html-mobile-card-body">
          <div v-for="field in card.fields" :key="field.key" class="html-mobile-card-field" :class="{ 'html-mobile-card-field-primary': field.role === 'primary' }">
            <span class="html-mobile-card-label">{{ field.label }}</span>
            <span class="html-mobile-card-value" :class="field.classes" v-html="field.html" @click="handleFieldClick($event, card, field)"
              @input="handleFieldControlEvent($event, card, field, 'input')"
              @change="handleFieldControlEvent($event, card, field, 'change')"
              @keypress="handleFieldControlEvent($event, card, field, 'keypress')"
            ></span>
          </div>
        </div>
        <div v-if="card.actions.length" class="html-mobile-card-footer">
          <component
            :is="action.tag"
            v-for="(action, actionIndex) in card.actions"
            :key="`${actionIndex}-${action.text}`"
            v-bind="action.attrs"
            :class="action.useSourceStyles
              ? action.classes
              : ['html-mobile-card-action', action.classes, `html-mobile-card-action-${action.kind}`]"
            @click="handleActionClick($event, card, action)"
          >
            <span v-if="action.iconHtml" v-html="action.iconHtml"></span>
            <template v-else>{{ action.text }}</template>
          </component>
        </div>
      </article>
      <div v-if="!cards.length" class="html-mobile-cards-empty">{{ emptyText }}</div>
    </section>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
  tableId: { type: String, required: true },
  breakpoint: { type: Number, default: 768 },
  columnOptions: { type: Object, default: () => ({}) },
  groupRows: { type: Boolean, default: false },
  groupLabel: { type: String, default: '' },
  emptyText: { type: String, default: 'No data available' },
  horizontalOffset: { type: Number, default: 0 },
  preserveTextActions: { type: Boolean, default: false },
});

const emit = defineEmits(['action', 'selection-change']);
const cards = ref([]);
const portalTarget = ref(null);
let tableElement = null;
let wrapperElement = null;
let mediaQuery = null;
let observer = null;
let refreshQueued = false;

const getColumnOverride = (index, name) => props.columnOptions[name] || props.columnOptions[index] || props.columnOptions[String(index)] || {};

const getHeaderLabel = (header, fallback = '') => {
  if (!header) return fallback;
  const clone = header.cloneNode(true);
  clone.querySelectorAll('input, button, .checkbox').forEach((element) => element.remove());
  return clone.textContent.replace(/\s+/g, ' ').trim() || fallback;
};

const isInsideNestedModal = (element, root) => {
  const modal = element?.closest?.('.modal');
  return Boolean(modal && root?.contains(modal));
};

const getCellHtml = (cell) => {
  if (!cell) return '';
  const sourceControls = [...cell.querySelectorAll('input, select, textarea')]
    .filter((element) => !isInsideNestedModal(element, cell));
  const clone = cell.cloneNode(true);
  clone.querySelectorAll('.modal, script, style').forEach((element) => element.remove());
  const clonedControls = [...clone.querySelectorAll('input, select, textarea')];

  clonedControls.forEach((control, index) => {
    const source = sourceControls[index];
    if (!source) return;

    if (control.matches('input[type="checkbox"], input[type="radio"]')) {
      control.toggleAttribute('checked', source.checked);
    } else if (control.tagName === 'SELECT') {
      [...control.options].forEach((option, optionIndex) => {
        option.toggleAttribute('selected', source.options[optionIndex]?.selected === true);
      });
    } else if (control.tagName === 'TEXTAREA') {
      control.textContent = source.value;
    } else if (control.type !== 'file') {
      control.setAttribute('value', source.value);
    }

    control.toggleAttribute('disabled', source.disabled);
  });

  clone.querySelectorAll('[id]').forEach((element) => {
    if (element.matches('input[type="number"], input[type="text"], select, textarea')) {
      return;
    }
    element.removeAttribute('id');
  });
  return clone.innerHTML;
};

const getColumnRole = (column, firstHtml) => {
  if (column.override.role) return column.override.role;
  const classes = column.header.className || '';
  if (classes.includes('mobile-card-hidden')) return 'hidden';
  if (classes.includes('mobile-card-selection')) return 'selection';
  if (classes.includes('mobile-card-actions')) return 'actions';
  if (classes.includes('mobile-card-primary')) return 'primary';
  if (column.header.querySelector('input[type="checkbox"], input[type="radio"]')) return 'selection';
  const label = column.label.toLowerCase().replace(/\s+/g, ' ').trim();
  if (/^actions?$/.test(label)) return 'actions';
  if (!label && /type=["'](?:checkbox|radio)["']/i.test(firstHtml)) return 'selection';
  if (firstHtml.includes('dropdown-menu') && /<(?:a|button)\b/i.test(firstHtml)) return 'actions';
  return 'field';
};

const getSelection = (cell, columnIndex) => {
  const input = cell?.querySelector('input[type="checkbox"], input[type="radio"]');
  if (!input) return null;
  const attrs = {};
  for (const attribute of input.attributes) {
    if (!['id', 'type', 'checked', 'class'].includes(attribute.name)) attrs[attribute.name] = attribute.value;
  }
  return { columnIndex, attrs, checked: input.checked };
};

const getActionElements = (cell) => {
  if (!cell) return [];
  const root = cell.querySelector('.dropdown-menu') || cell;
  return [...root.querySelectorAll('a, button')].filter((element) => element.getAttribute('data-toggle') !== 'dropdown');
};

const getActionKind = (element) => {
  const text = [element.className, element.title, element.textContent, element.querySelector('i, svg')?.className].filter(Boolean).join(' ').toLowerCase();
  if (/delete|remove|trash/.test(text)) return 'delete';
  if (/edit|update|pencil|pen/.test(text)) return 'edit';
  if (/view|detail|show|eye/.test(text)) return 'view';
  if (/download/.test(text)) return 'download';
  return 'default';
};

const fallbackActionIcons = {
  view: '<i class="fas fa-eye"></i>',
  edit: '<i class="fas fa-pencil-alt"></i>',
  delete: '<i class="fas fa-trash-alt"></i>',
  download: '<i class="fas fa-download"></i>',
  default: '<i class="fas fa-ellipsis-h"></i>',
};

const getActions = (cell, columnIndex) => getActionElements(cell).map((element, sourceIndex) => {
  const attrs = {};
  for (const attribute of element.attributes) {
    if (!['id', 'class', 'style', 'data-toggle', 'aria-expanded'].includes(attribute.name)) attrs[attribute.name] = attribute.value;
  }

  const icon = element.querySelector('svg, i');
  const hasSourceIcon = Boolean(icon);
  const useSourceStyles = hasSourceIcon || props.preserveTextActions;
  const classes = useSourceStyles
    ? [...element.classList]
    : [...element.classList].filter((name) => !['dropdown-item', 'dropdown-toggle', 'btn', 'btn-sm', 'btn-clean', 'btn-icon', 'btn-icon-md'].includes(name));
  const tag = element.tagName.toLowerCase();
  const kind = getActionKind(element);
  if (tag === 'button' && !attrs.type) attrs.type = 'button';
  return { tag, attrs, classes, useSourceStyles, iconHtml: icon?.outerHTML || (props.preserveTextActions ? '' : fallbackActionIcons[kind]), text: element.textContent.replace(/\s+/g, ' ').trim() || element.title || 'Action', kind, columnIndex, sourceIndex };
});

const buildCards = () => {
  if (!mediaQuery?.matches || !tableElement) { cards.value = []; return; }
  const headerRow = tableElement.tHead?.rows?.[0];
  const body = tableElement.tBodies?.[0];
  if (!headerRow || !body) { cards.value = []; return; }
  const rows = [...body.rows].filter((row) => row.parentElement === body && !row.classList.contains('mobile-card-ignore-row'));
  const firstRow = rows[0];
  const columns = [...headerRow.cells].map((header, index) => {
    const name = header.dataset.name || '';
    const override = getColumnOverride(index, name);
    const label = String(override.label !== undefined ? override.label : getHeaderLabel(header, name));
    const column = { index, name, header, override, label, role: null };
    const available = !header.hidden && !header.classList.contains('d-none') && window.getComputedStyle(header).display !== 'none';
    column.role = available ? getColumnRole(column, getCellHtml(firstRow?.cells?.[index])) : 'hidden';
    return column;
  });

  cards.value = rows.map((row, rowIndex) => {
    const fields = [];
    const actions = [];
    let selection = null;
    columns.forEach((column) => {
      if (column.role === 'hidden') return;
      const cell = row.cells[column.index];
      const html = getCellHtml(cell);
      if (column.role === 'selection') { selection ||= getSelection(cell, column.index); return; }
      if (column.role === 'actions') { actions.push(...getActions(cell, column.index)); return; }
      if (!html.trim() && column.override.showEmpty !== true) return;
      fields.push({ key: column.name || String(column.index), columnIndex: column.index, label: column.label, html, role: column.role, classes: [...(cell?.classList || [])] });
    });
    return { key: row.id || row.dataset.id || rowIndex, rowIndex, sourceRow: row, fields, actions, selection };
  });
};

const scheduleRefresh = () => {
  if (refreshQueued) return;
  refreshQueued = true;
  window.requestAnimationFrame(() => { refreshQueued = false; buildCards(); });
};

const updateMobileMode = () => {
  if (!wrapperElement || !mediaQuery) return;
  wrapperElement.classList.toggle('html-mobile-card-mode', mediaQuery.matches);
  scheduleRefresh();
};

const removeBindings = () => {
  observer?.disconnect();
  observer = null;
  tableElement?.classList.remove('html-mobile-card-source');
  wrapperElement?.classList.remove('html-mobile-card-wrapper', 'html-mobile-card-mode');
  if (portalTarget.value) {
    const portal = portalTarget.value;
    portalTarget.value = null;
    nextTick(() => portal.remove());
  }
  tableElement = null;
  wrapperElement = null;
  cards.value = [];
};

const attachToTable = () => {
  const table = document.getElementById(props.tableId);
  if (!table) return;
  if (tableElement === table) { scheduleRefresh(); return; }
  removeBindings();
  tableElement = table;
  wrapperElement = table.parentElement;
  if (!wrapperElement) return;
  wrapperElement.classList.add('html-mobile-card-wrapper');
  tableElement.classList.add('html-mobile-card-source');
  const portal = document.createElement('div');
  portal.className = 'html-mobile-card-portal';
  portal.dataset.tableId = props.tableId;
  tableElement.insertAdjacentElement('afterend', portal);
  portalTarget.value = portal;
  observer = new MutationObserver(scheduleRefresh);
  observer.observe(tableElement, { childList: true, subtree: true, characterData: true, attributes: true });
  updateMobileMode();
};

const configureMediaQuery = () => {
  if (mediaQuery) {
    if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', updateMobileMode);
    else mediaQuery.removeListener?.(updateMobileMode);
  }
  mediaQuery = window.matchMedia(`(max-width: ${props.breakpoint}px)`);
  if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', updateMobileMode);
  else mediaQuery.addListener?.(updateMobileMode);
  updateMobileMode();
};

const getSourceCell = (card, index) => card.sourceRow?.cells?.[index] || null;

const fieldControlSelector = 'input, select, textarea';
const fieldClickableSelector = 'a, button, input[type="button"], input[type="submit"], [data-toggle], [role="button"], span[type="button"]';
const getFieldElements = (root, selector) => [...(root?.querySelectorAll(selector) || [])]
  .filter((element) => !isInsideNestedModal(element, root));

const handleFieldControlEvent = (event, card, field, eventType) => {
  const cardControl = event.target.closest?.(fieldControlSelector);
  if (!cardControl || !event.currentTarget.contains(cardControl)) return;

  const cardControls = getFieldElements(event.currentTarget, fieldControlSelector);
  const sourceControls = getFieldElements(getSourceCell(card, field.columnIndex), fieldControlSelector);
  const sourceControl = sourceControls[cardControls.indexOf(cardControl)];
  if (!sourceControl) return;

  if (cardControl.type === 'checkbox' || cardControl.type === 'radio') {
    sourceControl.checked = cardControl.checked;
  } else {
    sourceControl.value = cardControl.value;
  }

  const forwardedEvent = eventType === 'keypress'
    ? new KeyboardEvent(eventType, {
        key: event.key,
        code: event.code,
        charCode: event.charCode,
        keyCode: event.keyCode,
        which: event.which,
        bubbles: true,
        cancelable: true,
      })
    : new Event(eventType, { bubbles: true, cancelable: true });

  sourceControl.dispatchEvent(forwardedEvent);
  if (forwardedEvent.defaultPrevented) event.preventDefault();
  if (eventType === 'change') nextTick(scheduleRefresh);
};

const handleFieldClick = (event, card, field) => {
  const clicked = event.target.closest?.(fieldClickableSelector);
  if (!clicked || !event.currentTarget.contains(clicked)) return;
  const cardElements = getFieldElements(event.currentTarget, fieldClickableSelector);
  const sourceElements = getFieldElements(getSourceCell(card, field.columnIndex), fieldClickableSelector);
  const source = sourceElements[cardElements.indexOf(clicked)];
  if (!source) return;
  event.preventDefault();
  event.stopPropagation();
  source.click();
};

const handleSelectionChange = (event, card) => {
  const sourceInput = getSourceCell(card, card.selection.columnIndex)?.querySelector('input[type="checkbox"], input[type="radio"]');
  if (sourceInput && sourceInput.checked !== event.target.checked) sourceInput.click();
  if (sourceInput) event.target.checked = sourceInput.checked;
  card.selection.checked = event.target.checked;
  scheduleRefresh();
  emit('selection-change', { checked: event.target.checked, rowIndex: card.rowIndex, sourceInput });
};

const handleActionClick = (event, card, action) => {
  const sourceAction = getActionElements(getSourceCell(card, action.columnIndex))[action.sourceIndex];
  if (sourceAction) {
    event.preventDefault();
    event.stopPropagation();
    sourceAction.click();
  }
  emit('action', { event, action, rowIndex: card.rowIndex });
};

onMounted(() => { configureMediaQuery(); nextTick(attachToTable); });
onBeforeUnmount(() => {
  if (mediaQuery) {
    if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', updateMobileMode);
    else mediaQuery.removeListener?.(updateMobileMode);
  }
  removeBindings();
});
watch(() => props.columnOptions, scheduleRefresh, { deep: true });
watch(() => props.breakpoint, configureMediaQuery);
watch(() => props.tableId, () => { removeBindings(); nextTick(attachToTable); });
</script>

<style>
.html-mobile-cards-anchor, .html-mobile-card-portal {
    display: none;
}
.html-mobile-card-wrapper.html-mobile-card-mode .html-mobile-card-source {
    display: none !important;
}
.html-mobile-card-wrapper.html-mobile-card-mode .html-mobile-card-portal {
    display: block;
    width: 100%;
    box-sizing: border-box;
}
.html-mobile-cards-list {
    width: 100%;
}
.html-mobile-card {
    width: 100%;
    margin-bottom: 20px;
    padding: 15px;
    background: #fff;
    border: 2px solid #e4e6ef;
    border-radius: 8px;
    box-sizing: border-box;
    box-shadow: 0 2px 8px rgba(0,0,0,.1);
}
.html-mobile-cards-grouped {
    padding: 15px;
    background: #fff;
    border: 2px solid #e4e6ef;
    border-radius: 8px;
    box-sizing: border-box;
    box-shadow: 0 2px 8px rgba(0,0,0,.1);
}
.html-mobile-cards-group-title {
    margin: 0;
    padding-bottom: 12px;
    color: #3f4254;
    font-weight: 700;
}
.html-mobile-cards-grouped .html-mobile-card {
    margin: 0;
    padding: 15px 0;
    border: 0;
    border-top: 1px solid #e4e6ef;
    border-radius: 0;
    box-shadow: none;
}
.html-mobile-card-header {
    min-height: 24px;
    margin-bottom: 4px;
}
.html-mobile-card-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
}
.html-mobile-card-label {
    flex: 0 1 auto;
    max-width: 75%;
    color: #7e8299;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    white-space: normal;
    overflow-wrap: anywhere;
}
.html-mobile-card-value {
    min-width: 0;
    flex: 1;
    color: #3f4254;
    font-size: 14px;
    font-weight: 500;
    text-align: right !important;
    overflow-wrap: anywhere;
}
.html-mobile-card-value > .d-flex {
    justify-content: flex-end;
}
.html-mobile-card-field-primary .html-mobile-card-label {
    display: none;
}
.html-mobile-card-field-primary .html-mobile-card-value {
    flex-basis: 100%;
    text-align: left !important;
}
.html-mobile-card-field-primary .html-mobile-card-value > .d-flex {
    justify-content: flex-start;
}
.html-mobile-card-field-primary .html-mobile-card-value > .row {
    flex-wrap: nowrap;
    align-items: center;
    margin-right: 0;
    margin-left: 0;
}
.html-mobile-card-field-primary .html-mobile-card-value > .row > [class*="col-"] {
    width: auto;
    max-width: none;
}
.html-mobile-card-field-primary .html-mobile-card-value > .row > [class*="col-"]:first-child {
    flex: 0 0 auto;
    padding-right: 10px;
    padding-left: 0;
}
.html-mobile-card-field-primary .html-mobile-card-value > .row > [class*="col-"]:last-child {
    flex: 1 1 auto;
    min-width: 0;
    padding-right: 0;
    padding-left: 0;
}
.html-mobile-card-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 25px;
    padding-top: 12px;
}
.html-mobile-card-action {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin: 0 !important;
    padding: 0 !important;
    border: 0;
    border-radius: 50%;
    text-decoration: none !important;
}
.html-mobile-card-action i {
    margin: 0 !important;
    color: inherit !important;
}
.html-mobile-card-action-view {
    color: #3699ff !important;
    background: #e8f0fe;
}
.html-mobile-card-action-edit {
    color: #34f95f !important;
    background: #e8f0fe;
}
.html-mobile-card-action-delete {
    color: #f92d37 !important;
    background: #e8f0fe;
}
.html-mobile-card-action-default {
    color: #3f4254 !important;
    background: #f3f6f9;
}
.html-mobile-card-action-download {
    color: #3f4254 !important;
    background: transparent;
}
.html-mobile-card-action-text {
    width: auto;
    min-width: 40px;
    padding: 0 12px !important;
    border-radius: 20px;
}
.html-mobile-cards-empty {
    padding: 20px 15px;
    color: #7e8299;
    text-align: center;
}
</style>